import {
  action, observable,
} from 'mobx';
import {
  ISurvey,
} from 'mobx-store/storeInterfaces/surveyInterface';
import {
  SurveyDTO,
} from 'screens/surveyManagementScreen/surveyDTO';
import {
  ICountry,
} from 'mobx-store/storeInterfaces/authInterface';
import {
  RequestStatus,
} from 'mobx-store';
import Connections from '../../connections';
import {
  createSurvey, getSurveyById, getSurveyReport, getSurveys, updateSurvey,
} from '../../axios/surveysRequests';
import {
  DefaultLanguage,
} from '../../constants/languages';
import {
  getHeader,
} from '../../helperFunctions/getHeader';
import FileSaver from 'file-saver';

export class SurveysStore {
  @observable surveysList: any[] = [];

  @observable status: RequestStatus = RequestStatus.IDLE;
  @observable createAndUpdateStatus: RequestStatus = RequestStatus.IDLE;

  @observable selectedSurvey: ISurvey = {
    surveyId: 0,
    title: "",
    description: "",
    isActive: false,
    availableCountries: ["EG"],
  };

  @action
  public async getSurveys(langCode: string = DefaultLanguage): Promise<void> {
    try {
      this.status = RequestStatus.LOADING;

      const config = {
        ...getHeader(langCode),
      };

      const response = await Connections.wadiDeglaAxios.httpGet(
        getSurveys,
        config
      );
      this.surveysList = response.data;
      this.status = RequestStatus.SUCCESS;
      return Promise.resolve();
    } catch (error) {
      this.status = RequestStatus.FAILED;
      return Promise.reject(error);
    }
  }

  @action
  public async getSurveyById(
    id: number,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.status = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        getSurveyById(id),
        getHeader(langCode)
      );
      this.selectedSurvey = this.restructureSurvey(response.data);
      this.status = RequestStatus.SUCCESS;
      return Promise.resolve();
    } catch (error) {
      this.status = RequestStatus.FAILED;
      return Promise.reject(error);
    }
  }

  @action
  public async createSurvey(
    survey: SurveyDTO,
    countries: ICountry[],
    langCode: string = DefaultLanguage
  ): Promise<number> {
    try {
      this.createAndUpdateStatus = RequestStatus.LOADING;
      const surveyToSend = JSON.parse(JSON.stringify(survey));
      surveyToSend.availableCountries = countries.map(
        (country: ICountry) => country.isoCode
      );
      surveyToSend.questions.map((question: any) => {
        [question.type] = question.type;
        [question.questionTextLength] = question.questionTextLength;
        if (question.type === "MCQ") {
          [question.previewType] = question.mcqPreviewType;
        } else if (question.type === "RATING") {
          [question.previewType] = question.ratingPreviewType;
        }
        delete question.ratingPreviewType;
        delete question.mcqPreviewType;
        return question;
      });
      const response = await Connections.wadiDeglaAxios.httpPost(
        createSurvey,
        surveyToSend,
        getHeader(langCode)
      );
      this.createAndUpdateStatus = RequestStatus.SUCCESS;
      return response.data;
    } catch (error) {
      console.log(error);
      this.createAndUpdateStatus = RequestStatus.FAILED;
      return Promise.reject(error);
    }
  }

  @action
  public async updateSurvey(
    surveyId: number,
    survey: any,
    countries: ICountry[],
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.createAndUpdateStatus = RequestStatus.LOADING;
      const surveyToSend = JSON.parse(JSON.stringify(survey));

      delete surveyToSend.id;
      surveyToSend.availableCountries = countries.map(
        (country: ICountry) => country.isoCode
      );
      if (surveyToSend.questions) {
        surveyToSend.questions.map((question: any) => {
          [question.type] = question.type;
          [question.questionTextLength] = question.questionTextLength;
          if (question.type === "MCQ") {
            [question.previewType] = question.mcqPreviewType;
          } else if (question.type === "RATING") {
            [question.previewType] = question.ratingPreviewType;
          }
          delete question.ratingPreviewType;
          delete question.mcqPreviewType;
          return question;
        });
      }

      await Connections.wadiDeglaAxios.httpPut(
        updateSurvey(surveyId),
        surveyToSend,
        getHeader(langCode)
      );
      this.createAndUpdateStatus = RequestStatus.SUCCESS;
      return Promise.resolve();
    } catch (error) {
      this.createAndUpdateStatus = RequestStatus.FAILED;
      return Promise.reject(error);
    }
  }

  private restructureSurvey(survey: any): any {
    const restructuredSurvey = {
      id: survey.surveyId,
      surveyId: survey.surveyId,
      title: survey.title,
      description: survey.description,
      questionsForm: survey.questionsForm,
      questionsFormId: survey.questionsForm.questionsFormId,
      questions: survey.questionsForm.questions.map((question: any) => ({
        type: [question.type],
        isMandatory: question.isMandatory,
        isOther: question?.isOther || false,
        isNotApplicable: question?.isNotApplicable || false,
        options: question.questionOptions,
        title: question.title,
        description: question?.description || "",
        questionId: question.questionId,
        sharedQuestionsFormContentId: question.sharedQuestionsFormContentId,
        ratingPreviewType:
          question.previewType && question.type === "RATING"
            ? [question.previewType]
            : [],
        mcqPreviewType:
          question.previewType && question.type === "MCQ"
            ? [question.previewType]
            : [],
        questionTextLength: question?.questionTextProperty?.length
          ? [question?.questionTextProperty?.length]
          : [],
      })),
      isActive: survey.isActive,
    };
    return restructuredSurvey;
  }

  @action
  public async getSurveyReport(
    surveyId: number,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.status = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        getSurveyReport(surveyId),
        {
          responseType: "blob",
          headers: {
            "accept-language": langCode,
            "x-apicache-bypass": true,
          },
        }
      );
      await FileSaver.saveAs(response, "survey-report.csv");
      this.status = RequestStatus.SUCCESS;
      return Promise.resolve();
    } catch (error) {
      this.status = RequestStatus.FAILED;
      return Promise.reject(error);
    }
  }
}
